import { useCallback, useEffect, useMemo, useState } from 'react';
import Plausible from 'plausible-tracker';
import TagManager from 'react-gtm-module';

import { TrackingContext, LOCATION_CHANNEL_KEY } from './Tracking.context';
import config from '../config';
import { TrackingEvent, TrackerLogger } from './Tracking.events';
import { Trackers } from './Trackers';

const ANALYTICS_ENABLED_KEY = 'analytics_enabled';
const ANALYTICS_CHANNEL_KEY = 'analytics';
const ANALYTICS_ENABLED = 'enable';
const ANALYTICS_DISABLE = 'disable';

declare global {
	const OnetrustActiveGroups: any;
	const a360_targetting_cookies: any;
	interface Window {
		a360_targetting_cookies: any;
	}
	interface Window {
		gtag: any;
		_paq: [string, ...any[]][];
	}
}

export function Tracking({ children }: TrackingProps) {
	const { analytics } = config;
	const [history, setHistory] = useState([]);
	const [gtag, setGtag] = useState(null);
	const [gtm, setGTM] = useState(null);
	const [plausible, setPlausible] = useState(null);
	const [matomo, setMatomo] = useState(null);
	const [locationChannel] = useState(
		new BroadcastChannel(LOCATION_CHANNEL_KEY),
	);
	const [analyticsChannel] = useState(
		new BroadcastChannel(ANALYTICS_CHANNEL_KEY),
	);

	const trackerOptions = {
		analytics,
		setGtag,
		setGTM,
		setMatomo,
		setPlausible,
	};
	useEffect(() => {
		initializeTrackers(trackerOptions);
		// TODO this is only aplicable for A360
		// if (localStorage.getItem(ANALYTICS_ENABLED_KEY)) {
		// 	initializeTrackers(trackerOptions);
		// }
		analyticsChannel.onmessage = ({ data }) => {
			if (
				data === ANALYTICS_DISABLE &&
				localStorage.getItem(ANALYTICS_ENABLED_KEY)
			) {
				disableTrackers(trackerOptions);
				// location.reload();
				return;
			}
			if (
				data === ANALYTICS_ENABLED &&
				!localStorage.getItem(ANALYTICS_ENABLED_KEY)
			) {
				initializeTrackers(trackerOptions);
				// location.reload();
				return;
			}
		};
	}, []);

	const trackers: Trackers = useMemo(() => {
		if (window.gtag) {
			console.log(window.gtag);
		}
		return new Trackers({
			gtag,
			gtm,
			matomo,
			plausible,
		});
	}, [gtag, gtm, matomo, plausible]);

	const sendEvent = useCallback(
		(event: TrackingEvent) => {
			trackers.sendEvent(event);
			analyticsChannel.postMessage(event);
		},
		[trackers],
	);

	return (
		<TrackingContext.Provider
			value={{
				gtag,
				gtm,
				plausible,
				history,
				setHistory,
				locationChannel,
				matomo,
				sendEvent,
			}}
		>
			{children}
		</TrackingContext.Provider>
	);
}

function initializeTrackers(args: TrackersOptions) {
	const { analytics, setGtag, setGTM, setPlausible } = args;
	TrackerLogger.info(`Initialize trackers: \n`, analytics);
	if (analytics?.gtag?.trackerID) {
		initializeGtag(setGtag);
	}

	if (analytics?.gtm?.trackerID) {
		initializeGTM(setGTM);
	}

	if (analytics?.plausible?.domain) {
		initializePlausible(setPlausible);
	}

	/*if (analytics?.matomo?.src) {
		initializeMatomo(setMatomo);
	}*/
	localStorage.setItem(ANALYTICS_ENABLED_KEY, 'true');
}

function disableTrackers(args: TrackersOptions) {
	const { setGtag, setGTM, setMatomo, setPlausible } = args;
	TrackerLogger.info(`I disable all the analytics`);
	setGtag(null);
	setGTM(null);
	setMatomo(null);
	setPlausible(null);
	localStorage.removeItem(ANALYTICS_ENABLED_KEY);
}

interface TrackersOptions {
	analytics: any;
	setGtag: any;
	setGTM: any;
	setMatomo: any;
	setPlausible: any;
}

interface TrackingProps {
	children: string | JSX.Element | JSX.Element[];
}

function initializeGtag(setter: (gtag: any) => void) {
	/*let sampleRate = 1;
	if (
		!!analytics.ga4.sampleRate &&
		typeof analytics.ga4.sampleRate === 'number'
	) {
		if (analytics.ga4.sampleRate > 0 && analytics.ga4.sampleRate < 100) {
			sampleRate = analytics.ga4.sampleRate;
		}
	}*/

	if (window.gtag) {
		setter(true);
		return;
	}

	const interval = setInterval(() => {
		if (window.gtag) {
			clearInterval(interval);
			return;
		}

		setter(true);
	}, 500);
}

function initializeGTM(setter: (gtm: any) => void) {
	const { analytics } = config;
	const tagManagerArgs = {
		gtmId: analytics?.gtm?.trackerID,
	};
	TagManager.initialize(tagManagerArgs);
	setter(TagManager);
	return;
}

function initializePlausible(setter: (plausible: any) => void) {
	const { analytics } = config;
	const domain = analytics?.plausible?.domain;

	const plausible = Plausible({
		domain: domain,
		trackLocalhost: true,
	});

	setter(plausible);
	return;
}

/*function initializeMatomo(setter: (matomo: any) => void) {
	// const { analytics } = config;
	// TODO Implement Matomo
	const matomoUrl = analytics.matomo.src;
	const matomoTracker = analytics.matomo.tracker;
	const siteId = analytics.matomo.siteId;

	const doc = document;
	const script = doc.createElement('script');
	const scripts = doc.getElementsByTagName('script')[0];

	script.type = 'text/javascript';
	script.async = true;
	script.defer = true;
	script.src = `${matomoUrl}`;

	window._paq = window._paq || [];
	window._paq.push(['trackPageView']);
	window._paq.push(['enableLinkTracking']);
	window._paq.push(['setTrackerUrl', `${matomoTracker}`]);
	window._paq.push(['setSiteId', siteId]);

	if (scripts && scripts.parentNode) {
		scripts.parentNode.insertBefore(script, scripts);
	}

	setter(window._paq);

	return;
}*/
